import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Search, X, Play, ExternalLink } from "lucide-react";
import { Input } from "../components/ui/input";
import { YOUTUBE_CHANNEL } from "../lib/branches";

const HERO = "https://images.unsplash.com/photo-1769755409781-9e8924c57362?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxwcmVhY2hpbmclMjBzZXJtb24lMjBzdGFnZXxlbnwwfHx8fDE3NzY1MzkzNzZ8MA&ixlib=rb-4.1.0&q=85";

export default function Sermons() {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeSeries, setActiveSeries] = useState("All");

  useEffect(() => {
    api.get("/sermons").then((r) => setSermons(r.data)).finally(() => setLoading(false));
  }, []);

  const seriesList = useMemo(() => {
    const set = new Set(sermons.map((s) => s.series).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [sermons]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return sermons.filter((s) => {
      if (activeSeries !== "All" && s.series !== activeSeries) return false;
      if (!needle) return true;
      return (
        s.title?.toLowerCase().includes(needle) ||
        s.preacher?.toLowerCase().includes(needle) ||
        s.description?.toLowerCase().includes(needle) ||
        s.series?.toLowerCase().includes(needle)
      );
    });
  }, [sermons, q, activeSeries]);

  return (
    <div data-testid="sermons-page">
      {/* header */}
      <section className="relative bg-olive text-bone">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO} alt="sermons" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <p className="overline text-terracotta mb-4">The Word</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
            Sermons <em className="italic font-light text-terracotta">&</em> Teachings
          </h1>
          <p className="mt-6 text-bone/80 text-lg max-w-2xl">
            Faith comes by hearing. Watch the latest messages on our YouTube channel.
          </p>
          <a
            href={YOUTUBE_CHANNEL}
            target="_blank"
            rel="noreferrer"
            data-testid="sermons-channel-link"
            className="mt-8 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-6 h-12 font-semibold transition-colors"
          >
            <Play size={16} fill="currentColor" /> Visit our YouTube channel <ExternalLink size={14} />
          </a>
        </div>
      </section>

      {/* search + series filters */}
      <section className="sticky top-[68px] z-30 bg-bone/90 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="relative lg:max-w-sm w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted_ink" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search sermons, preachers, series…"
              data-testid="sermons-search"
              className="pl-9 pr-9 h-11 bg-white"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted_ink hover:text-ink">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap lg:ml-auto">
            {seriesList.map((s) => (
              <button
                key={s}
                onClick={() => setActiveSeries(s)}
                data-testid={`series-filter-${s}`}
                className={`px-4 py-2 text-xs font-bold tracking-wider uppercase transition-colors ${
                  activeSeries === s
                    ? "bg-olive text-bone"
                    : "bg-white border border-border text-ink/70 hover:border-olive"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        {loading ? (
          <p className="text-muted_ink">Loading sermons…</p>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <p className="font-heading text-3xl text-olive">No sermons found.</p>
            <p className="text-muted_ink mt-2">Try a different search or series.</p>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-8">
            {filtered.map((s, i) => {
              const span = i === 0 ? "col-span-12 lg:col-span-8" : i % 3 === 1 ? "col-span-12 md:col-span-6 lg:col-span-4" : "col-span-12 md:col-span-6 lg:col-span-6";
              return (
                <a
                  key={s.id}
                  href={YOUTUBE_CHANNEL}
                  target="_blank"
                  rel="noreferrer"
                  className={`${span} bg-white border border-border hover:shadow-lg hover:-translate-y-1 transition-all block group`}
                  data-testid={`sermon-${s.id}`}
                >
                  <div className="aspect-video bg-olive relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-olive/40 via-olive to-olive-hover" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-terracotta text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play size={28} fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 bg-black/60 text-white text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 flex items-center gap-1">
                      YouTube <ExternalLink size={10} />
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 flex-wrap">
                      {s.series && (
                        <span className="text-[10px] font-bold tracking-widest uppercase bg-olive text-bone px-2.5 py-1">
                          {s.series}
                        </span>
                      )}
                      <p className="text-xs font-bold text-terracotta tracking-widest uppercase">
                        {new Date(s.date).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })} · {s.preacher}
                      </p>
                    </div>
                    <h3 className="font-heading font-bold text-2xl text-ink mt-3 group-hover:text-olive transition-colors">{s.title}</h3>
                    {s.description && <p className="text-sm text-muted_ink mt-2">{s.description}</p>}
                    <p className="mt-4 text-xs text-olive font-semibold flex items-center gap-1 group-hover:text-terracotta transition-colors">
                      Watch on YouTube <ExternalLink size={12} />
                    </p>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
